import { LRUCache } from 'lru-cache';
import { useMemo } from 'react';
import { Observable } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { useSocketClient } from '../providers/WebSocketClientProvider';
import type { MarketDataSnapshot, RequestStream, SubscriptionResponse } from '../types';
import type { KeyedMarketDataSnapshot, UseMultiMarketDataSnapshotOutput } from './useMultiMarketDataSnapshot';

interface UseMultiMarketDataSnapshotBaseParams<TRequest, TSubscriptionRequest extends RequestStream> {
  /** One request entry per MDS stream. This should be a ReplaySubject. */
  requestsObs: Observable<TRequest[]>;
  /** The maximum amount of subscriptions we dare keep open with the backend. */
  maxActiveSubscriptions: number;
  /**
   * Returns the logical request key for a wrapper-level request.
   * The base hook uses this key to dedupe websocket subscriptions, evict stale cache entries,
   * and attach `requestKey` to every emitted snapshot.
   */
  getRequestKey: (request: TRequest) => string;
  /**
   * Converts the wrapper-level request into the backend websocket request.
   * This should preserve the same identity represented by `getRequestKey`; otherwise the cache key
   * and the backend subscription would describe different market-data streams.
   */
  buildSubscriptionRequest: (request: TRequest) => TSubscriptionRequest | undefined;
}

/**
 * Shared websocket/cache implementation for the regular and customer multi-MDS hooks.
 *
 * `useMultiMarketDataSnapshot` and `useMultiCustomerMarketDataSnapshot` differ in how they build
 * backend requests and request keys, but both rely on this base hook to keep one active subscription
 * per request key, remove subscriptions whose keys disappeared, and publish snapshots keyed by that
 * same request key for downstream cache lookup.
 */
export function useMultiMarketDataSnapshotBase<TRequest, TSubscriptionRequest extends RequestStream>({
  requestsObs,
  maxActiveSubscriptions,
  getRequestKey,
  buildSubscriptionRequest,
}: UseMultiMarketDataSnapshotBaseParams<TRequest, TSubscriptionRequest>) {
  const websocketClient = useSocketClient();

  const marketDataObs = useMemo(
    () =>
      new Observable<UseMultiMarketDataSnapshotOutput>(output => {
        const activeSubscriptionsByKey = new LRUCache<string, string>({
          max: maxActiveSubscriptions,
          dispose: (address, key) => {
            marketDataByKey.delete(key);
            websocketClient.unregisterSubscription(address);
          },
          updateAgeOnHas: true,
        });

        const marketDataByKey = new Map<string, KeyedMarketDataSnapshot>();

        const requestsSub = requestsObs.subscribe(newRequests => {
          const newRequestKeys = new Set(newRequests.map(requestItem => getRequestKey(requestItem)).filter(Boolean));
          for (const key of activeSubscriptionsByKey.keys()) {
            if (!newRequestKeys.has(key)) {
              activeSubscriptionsByKey.delete(key);
            }
          }

          for (const requestItem of newRequests) {
            const key = getRequestKey(requestItem);
            const isRequestRegistered = activeSubscriptionsByKey.has(key);
            if (isRequestRegistered || !key) {
              continue;
            }

            const request = buildSubscriptionRequest(requestItem);
            if (!request) {
              continue;
            }

            const address = uuid();
            websocketClient.registerSubscription(address, [request], (err, json) => {
              if (err) {
                websocketClient.unregisterSubscription(address);
                activeSubscriptionsByKey.delete(key);
              }

              if (json && json.error) {
                //
              } else if (json && json.data && Array.isArray(json.data)) {
                const data = (json.data as MarketDataSnapshot[]).map(update => ({ ...update, requestKey: key }));
                for (const keyedSnapshot of data) {
                  marketDataByKey.set(key, keyedSnapshot);
                }
                output.next({
                  cache: marketDataByKey,
                  json: { ...json, data, initial: false } as SubscriptionResponse<KeyedMarketDataSnapshot>,
                });
              }
            });
            activeSubscriptionsByKey.set(key, address);
          }
        });

        return () => {
          requestsSub.unsubscribe();
          activeSubscriptionsByKey.forEach(address => websocketClient.unregisterSubscription(address));
        };
      }),
    [buildSubscriptionRequest, getRequestKey, maxActiveSubscriptions, requestsObs, websocketClient]
  );

  return marketDataObs;
}
